<script lang="ts">
import { Icon, Tooltip } from '@viamrobotics/prime-core';
import { clickOutside } from '@viamrobotics/prime-core';

import { Link } from '$lib/components/link';
import { useTooltipWarmup } from '$lib/tooltip-warmup.svelte';

interface LinkDetail {
  name: string;
  href: string;
  isOmitted?: boolean;
  extraPaths?: readonly string[];
  tooltip?: string;
}

const { currentPath, ariaLabel, linkDetails } = $props<{
  currentPath: string;
  ariaLabel: string;
  linkDetails: readonly LinkDetail[];
}>();

const links = $derived(
  linkDetails.filter(({ isOmitted }: { isOmitted?: boolean }) => !isOmitted)
);
const currentLink = $derived(
  links.find(
    ({ href, extraPaths }: { href: string; extraPaths?: readonly string[] }) =>
      currentPath.startsWith(href) ||
      extraPaths?.some((extraPath: string) => currentPath.startsWith(extraPath))
  ) ?? links[0]
);

// Mobile menu state
let mobileMenuOpen = $state(false);

const toggleMobileMenu = () => {
  mobileMenuOpen = !mobileMenuOpen;
};

const closeMobileMenu = () => {
  mobileMenuOpen = false;
};

const warmup = useTooltipWarmup(800);
</script>

<nav
  aria-label={ariaLabel}
  class="flex h-10 flex-none items-center border-b bg-extralight"
>
  <!-- Mobile Navigation -->
  <div
    class="flex sm:hidden relative w-full px-1 items-center"
    use:clickOutside={closeMobileMenu}
  >
    <button
      aria-label="Toggle navigation menu"
      class="flex items-center justify-between px-3"
      onclick={toggleMobileMenu}
    >
      <span
        class="font-roboto-mono tracking-wide text-[13px] uppercase font-semibold truncate"
      >
        {currentLink?.name ?? 'Navigation'}
      </span>
      <Icon
        name="chevron-down"
        class="transition-transform ml-2 w-4 h-4 text-gray-7 {mobileMenuOpen
          ? 'rotate-180'
          : ''}"
      />
    </button>

    {#if mobileMenuOpen}
      <article
        class="absolute left-0 right-0 top-full mt-2 flex max-h-[80vh] flex-col items-stretch overflow-y-auto border border-medium bg-white text-default shadow-sm z-10"
      >
        <ul class="flex flex-col w-full font-roboto-mono tracking-wide my-2">
          {#each links as { name, href, extraPaths } (href)}
            {@const isCurrent =
              currentPath.startsWith(href) ||
              extraPaths?.some((extraPath: string) =>
                currentPath.startsWith(extraPath)
              )}
            {#if !isCurrent}
              <li>
                <Link
                  {href}
                  aria-current={isCurrent ? 'page' : 'false'}
                  class={[
                    'flex h-10 items-center px-4 text-[13px] uppercase hover:bg-ghost-light focus:bg-ghost-light active:bg-ghost-medium w-full',
                    isCurrent
                      ? 'font-semibold text-default bg-ghost-light'
                      : 'text-subtle-1',
                  ]}
                  onclick={closeMobileMenu}
                >
                  <span class="truncate">{name}</span>
                </Link>
              </li>
            {/if}
          {/each}
        </ul>
      </article>
    {/if}
  </div>

  <!-- Desktop Navigation -->
  <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
  <ul
    class="hidden sm:flex w-full items-center font-roboto-mono tracking-wide sm:px-3 gap-2"
    onmouseenter={warmup.onGroupMouseEnter}
    onmouseleave={warmup.onGroupMouseLeave}
  >
    {#each links as { name, href, extraPaths, tooltip } (href)}
      {@const isCurrent =
        currentPath.startsWith(href) ||
        extraPaths?.some((extraPath: string) =>
          currentPath.startsWith(extraPath)
        )}
      <li class="overflow-hidden">
        <Tooltip
          location="bottom"
          hoverDelayMS={warmup.delay}
          state={tooltip ? undefined : 'invisible'}
          let:tooltipID
        >
          <Link
            {href}
            aria-current={isCurrent ? 'page' : 'false'}
            aria-describedby={tooltipID}
            class={[
              'flex h-8 items-center overflow-hidden px-2 text-[13px] tracking-wider uppercase hover:bg-ghost-light focus:bg-ghost-light sm:px-3 rounded-sm',
              isCurrent ? 'font-bold text-default' : 'text-subtle-2',
            ]}
          >
            <span class="truncate">{name}</span>
          </Link>
          <p
            slot="description"
            class="font-sans tracking-normal normal-case"
          >
            {tooltip}
          </p>
        </Tooltip>
      </li>
    {/each}
  </ul>
</nav>
